import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StripeConnectSection from '../../components/StripeConnectSection';
import { TZ } from '../../utils/date';

const CarpoolPartner = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [rides, setRides] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentRide, setCurrentRide] = useState(null);
  const [formData, setFormData] = useState({
    departure: '',
    destination: '',
    departure_time: '',
    available_seats: 4,
    price_per_seat: '',
    vehicle_model: '',
    vehicle_plate: '',
    description: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, ridesRes] = await Promise.all([
        api.get('/covoiturage/partner/stats'),
        api.get('/covoiturage/partner/rides'),
      ]);
      setStats(statsRes.data.stats || {});
      setRides(ridesRes.data.rides || []);
    } catch (err) {
      setStats({});
      setRides([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (ride = null) => {
    if (ride) {
      setCurrentRide(ride);
      setFormData({
        departure: ride.departure,
        destination: ride.destination,
        departure_time: ride.departure_time,
        available_seats: ride.available_seats,
        price_per_seat: ride.price_per_seat,
        vehicle_model: ride.vehicle.split(' - ')[0],
        vehicle_plate: ride.vehicle.split(' - ')[1],
        description: ride.description || '',
      });
    } else {
      setCurrentRide(null);
      setFormData({
        departure: '',
        destination: '',
        departure_time: '',
        available_seats: 4,
        price_per_seat: '',
        vehicle_model: '',
        vehicle_plate: '',
        description: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentRide(null);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = () => {
    console.log('Trajet:', currentRide ? 'Modifié' : 'Créé', formData);
    handleCloseDialog();
    loadData();
  };

  const handleDeleteRide = (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce trajet ?')) {
      console.log('Trajet supprimé:', id);
      loadData();
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'completed':
        return 'default';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active':
        return 'Actif';
      case 'completed':
        return 'Terminé';
      case 'cancelled':
        return 'Annulé';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', py: 4 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" fontWeight={700} gutterBottom sx={{ color: 'white' }}>
            Tableau de bord Covoiturage
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gérez vos trajets et suivez vos revenus
          </Typography>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                background: 'rgba(255, 255, 255, 0.03)',
                backdropFilter: 'blur(30px)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
              }}
            >
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Box
                    sx={{
                      bgcolor: 'rgba(240, 235, 224, 0.15)',
                      p: 1.5,
                      borderRadius: 2,
                      color: '#F0EBE0',
                    }}
                  >
                    <DirectionsCarIcon />
                  </Box>
                  <Box>
                    <Typography variant="h4" fontWeight={700} sx={{ color: 'white' }}>
                      {stats.total_rides}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Trajets totaux
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                background: 'rgba(255, 255, 255, 0.03)',
                backdropFilter: 'blur(30px)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
              }}
            >
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Box
                    sx={{
                      bgcolor: 'rgba(240, 235, 224, 0.15)',
                      p: 1.5,
                      borderRadius: 2,
                      color: '#F0EBE0',
                    }}
                  >
                    <GroupIcon />
                  </Box>
                  <Box>
                    <Typography variant="h4" fontWeight={700} sx={{ color: 'white' }}>
                      {stats.total_passengers}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Passagers transportés
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                background: 'rgba(255, 255, 255, 0.03)',
                backdropFilter: 'blur(30px)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
              }}
            >
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Box
                    sx={{
                      bgcolor: 'rgba(240, 235, 224, 0.15)',
                      p: 1.5,
                      borderRadius: 2,
                      color: '#F0EBE0',
                    }}
                  >
                    <AttachMoneyIcon />
                  </Box>
                  <Box>
                    <Typography variant="h4" fontWeight={700} sx={{ color: 'white' }}>
                      {(stats.total_revenue / 1000).toFixed(0)}k
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Revenus (€)
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                background: 'rgba(255, 255, 255, 0.03)',
                backdropFilter: 'blur(30px)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
              }}
            >
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Box
                    sx={{
                      bgcolor: 'rgba(240, 235, 224, 0.15)',
                      p: 1.5,
                      borderRadius: 2,
                      color: '#F0EBE0',
                    }}
                  >
                    <TrendingUpIcon />
                  </Box>
                  <Box>
                    <Typography variant="h4" fontWeight={700} sx={{ color: 'white' }}>
                      {stats.average_rating}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Note moyenne
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Actions */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" fontWeight={600} sx={{ color: 'white' }}>
            Mes trajets ({stats.active_rides} actifs)
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              background: 'linear-gradient(135deg, #2D4A35 0%, #1E3525 100%)',
              color: 'white',
              fontWeight: 700,
              boxShadow: '0 4px 20px rgba(240, 235, 224, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1E3525 0%, #162B1D 100%)',
                boxShadow: '0 6px 24px rgba(240, 235, 224, 0.5)',
              },
            }}
          >
            Créer un trajet
          </Button>
        </Box>

        {/* Rides Table */}
        <TableContainer
          component={Paper}
          sx={{
            background: 'rgba(255, 255, 255, 0.03)',
            backdropFilter: 'blur(30px)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: 'white', fontWeight: 700 }}>Trajet</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }}>Départ</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }}>Places</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }}>Prix/Place</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }}>Véhicule</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }}>Statut</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rides.map((ride) => (
                <TableRow key={ride.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} sx={{ color: 'white' }}>
                      {ride.departure} → {ride.destination}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(ride.departure_time).toLocaleString('fr-FR', { timeZone: TZ })}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {ride.available_seats}/{ride.total_seats} disponibles
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} sx={{ color: '#F0EBE0' }}>
                      {ride.price_per_seat} €
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {ride.vehicle}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={getStatusLabel(ride.status)} color={getStatusColor(ride.status)} size="small" />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(ride)}
                        disabled={ride.status !== 'active'}
                        sx={{ color: '#F0EBE0' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteRide(ride.id)}
                        disabled={ride.status !== 'active'}
                        sx={{ color: '#e74c3c' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Paiements Stripe Connect */}
        <Box sx={{ mt: 4, mb: 4 }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>Paiements</Typography>
          <StripeConnectSection commissionRate={10} commissionLabel="par trajet" />
        </Box>

        {/* Create/Edit Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle sx={{ background: 'rgba(255, 255, 255, 0.03)', color: 'white' }}>
            {currentRide ? 'Modifier le trajet' : 'Créer un nouveau trajet'}
          </DialogTitle>
          <DialogContent sx={{ background: 'rgba(255, 255, 255, 0.03)', mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Départ"
                  name="departure"
                  value={formData.departure}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Destination"
                  name="destination"
                  value={formData.destination}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date et heure de départ"
                  name="departure_time"
                  type="datetime-local"
                  value={formData.departure_time}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Places disponibles"
                  name="available_seats"
                  type="number"
                  value={formData.available_seats}
                  onChange={handleChange}
                  inputProps={{ min: 1, max: 8 }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Prix par place (€)"
                  name="price_per_seat"
                  type="number"
                  value={formData.price_per_seat}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Modèle du véhicule"
                  name="vehicle_model"
                  value={formData.vehicle_model}
                  onChange={handleChange}
                  placeholder="Ex: Toyota Corolla"
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Plaque d'immatriculation"
                  name="vehicle_plate"
                  value={formData.vehicle_plate}
                  onChange={handleChange}
                  placeholder="Ex: AB-123-CD"
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description (optionnel)"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  placeholder="Informations supplémentaires sur le trajet..."
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ background: 'rgba(255, 255, 255, 0.03)', p: 2 }}>
            <Button onClick={handleCloseDialog}>Annuler</Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              sx={{
                background: 'linear-gradient(135deg, #2D4A35 0%, #1E3525 100%)',
                color: 'white',
              }}
            >
              {currentRide ? 'Modifier' : 'Créer'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default CarpoolPartner;
